import type {
  ActionResult,
  FulfillmentIgnoreRule,
  FulfillmentIgnoreRuleCreateResult,
  FulfillmentIgnoreRuleInput,
  FulfillmentLine,
  FulfillmentLineBulkActionResult,
  FulfillmentLineResult,
  FulfillmentRunDetail,
  FulfillmentRunListItem,
  FulfillmentRunResult,
  FulfillmentSettingsSummary,
  Paginated,
  ReturnCarrier,
  ReturnCarriersResponse,
  RunStatus,
  SchedulePresetInput,
} from '@flux/shared';
import { apiFetch, downloadFromApi } from '@/lib/api/client.js';

/** Couche réseau de l'écran Expéditions (`02` §6). Clés locales à cette fonctionnalité. */
export const fulfillmentQueryKeys = {
  all: ['fulfillment'] as const,
  settings: () => ['fulfillment', 'settings'] as const,
  runs: (params: RunsListParams) => ['fulfillment', 'runs', params] as const,
  runDetail: (id: string) => ['fulfillment', 'runs', id] as const,
  errorLines: (params: ErrorLinesParams) => ['fulfillment', 'error-lines', params] as const,
  returnCarriers: () => ['fulfillment', 'return-carriers'] as const,
  ignoreRules: () => ['fulfillment', 'ignore-rules'] as const,
};

export function fetchFulfillmentSettings(): Promise<FulfillmentSettingsSummary> {
  return apiFetch<FulfillmentSettingsSummary>('/api/fulfillment/settings');
}

export function updateFulfillmentMode(mode: 'simulation' | 'active'): Promise<FulfillmentSettingsSummary> {
  return apiFetch<FulfillmentSettingsSummary>('/api/fulfillment/mode', { method: 'PUT', body: { mode } });
}

export function updateFulfillmentEnabled(enabled: boolean): Promise<FulfillmentSettingsSummary> {
  return apiFetch<FulfillmentSettingsSummary>('/api/fulfillment/enabled', { method: 'PUT', body: { enabled } });
}

export interface ScheduleUpdateBody {
  schedule?: string;
  preset?: SchedulePresetInput;
}

export function updateFulfillmentSchedule(body: ScheduleUpdateBody): Promise<FulfillmentSettingsSummary> {
  return apiFetch<FulfillmentSettingsSummary>('/api/fulfillment/schedule', { method: 'PUT', body });
}

export function runFulfillmentNow(simulation: boolean): Promise<FulfillmentRunResult> {
  return apiFetch<FulfillmentRunResult>('/api/fulfillment/run', { method: 'POST', body: { simulation } });
}

export interface RunsListParams {
  page: number;
  pageSize: number;
  sortDir: 'asc' | 'desc';
  sortBy?: string;
  status?: RunStatus[];
  simulation?: boolean;
}

export function fetchFulfillmentRuns(params: RunsListParams): Promise<Paginated<FulfillmentRunListItem>> {
  return apiFetch<Paginated<FulfillmentRunListItem>>('/api/fulfillment/runs', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortDir: params.sortDir,
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.status && params.status.length > 0 ? { status: params.status } : {}),
      ...(params.simulation !== undefined ? { simulation: params.simulation } : {}),
    },
  });
}

export function fetchFulfillmentRunDetail(id: string): Promise<FulfillmentRunDetail> {
  return apiFetch<FulfillmentRunDetail>(`/api/fulfillment/runs/${id}`);
}

export function downloadFulfillmentRawFile(id: string): void {
  downloadFromApi(`/api/fulfillment/files/${id}/raw`);
}

export interface ErrorLinesParams {
  page: number;
  pageSize: number;
  sortDir: 'asc' | 'desc';
  sortBy?: string;
  store?: string;
  search?: string;
}

export function fetchFulfillmentErrorLines(params: ErrorLinesParams): Promise<Paginated<FulfillmentLine>> {
  return apiFetch<Paginated<FulfillmentLine>>('/api/fulfillment/lines', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortDir: params.sortDir,
      result: ['error'] satisfies FulfillmentLineResult[],
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.store ? { store: params.store } : {}),
      ...(params.search ? { search: params.search } : {}),
    },
  });
}

export function retryFulfillmentLines(lineIds: string[]): Promise<FulfillmentLineBulkActionResult> {
  return apiFetch<FulfillmentLineBulkActionResult>('/api/fulfillment/lines/retry', {
    method: 'POST',
    body: { lineIds },
  });
}

export function ignoreFulfillmentLines(lineIds: string[], reason: string): Promise<FulfillmentLineBulkActionResult> {
  return apiFetch<FulfillmentLineBulkActionResult>('/api/fulfillment/lines/ignore', {
    method: 'POST',
    body: { lineIds, reason },
  });
}

export function fetchReturnCarriers(): Promise<ReturnCarriersResponse> {
  return apiFetch<ReturnCarriersResponse>('/api/fulfillment/return-carriers');
}

export interface ReturnCarrierInput {
  pattern: string;
  company: string;
  order: number;
}

export function createReturnCarrier(body: ReturnCarrierInput): Promise<ReturnCarrier> {
  return apiFetch<ReturnCarrier>('/api/fulfillment/return-carriers', { method: 'POST', body });
}

export function updateReturnCarrier(id: string, body: ReturnCarrierInput): Promise<ReturnCarrier> {
  return apiFetch<ReturnCarrier>(`/api/fulfillment/return-carriers/${id}`, { method: 'PUT', body });
}

export function deleteReturnCarrier(id: string): Promise<ActionResult> {
  return apiFetch<ActionResult>(`/api/fulfillment/return-carriers/${id}`, { method: 'DELETE' });
}

export function updateDefaultReturnCarrier(company: string): Promise<ActionResult> {
  return apiFetch<ActionResult>('/api/fulfillment/return-carriers/default', { method: 'PUT', body: { company } });
}

/** Règles « commandes à ignorer » du Suivi Martelet (`01` §5.3.0). */
export function fetchIgnoreRules(): Promise<FulfillmentIgnoreRule[]> {
  return apiFetch<FulfillmentIgnoreRule[]>('/api/fulfillment/ignore-rules');
}

export function createIgnoreRule(body: FulfillmentIgnoreRuleInput): Promise<FulfillmentIgnoreRuleCreateResult> {
  return apiFetch<FulfillmentIgnoreRuleCreateResult>('/api/fulfillment/ignore-rules', { method: 'POST', body });
}

export function deleteIgnoreRule(id: string): Promise<ActionResult> {
  return apiFetch<ActionResult>(`/api/fulfillment/ignore-rules/${id}`, { method: 'DELETE' });
}
